import currency from "currency.js";
import {
  GetAccount200ResponsePositionsInner,
  SideResponse,
} from "../../../codegen-api";
import { IMarketPrecisionGetter } from "../../../contexts/MarketInstrumentContext/types";
import { IWSSMark } from "../../../hooks/wss/model/markPrice";
import { IPriceSize } from "../../../interfaces/Orderbook";
import { calculatePnl } from "../../../utils/math";
import { getValueOfContractsAmount } from "../../../utils/orderbook";
import { IPositionInfo, LiquidityTypeEnum } from "./types";

/* eslint-disable no-nested-ternary */

interface IComputePositionInfoParams {
  liquidity: string[][] | undefined;
  liquidityType: LiquidityTypeEnum;
  position: GetAccount200ResponsePositionsInner;
  bestLiquidity: string[];
  totalLiquiditySize: number;
  mark: IWSSMark | undefined;
  getMarketPrecision: IMarketPrecisionGetter;
}

export function computePositionInfo({
  liquidity,
  liquidityType,
  position,
  bestLiquidity,
  totalLiquiditySize,
  mark,
  getMarketPrecision,
}: IComputePositionInfoParams): IPositionInfo | undefined {
  let positionInfo: IPositionInfo | undefined;
  // Array of [price, size, iv]
  // if have a long position, sell into best BIDS
  // if have a short position, buy into best ASKS
  if (liquidity) {
    const positionEntryValue =
      Number(position.avg_entry_price) * Number(position.amount);

    switch (liquidityType) {
      case LiquidityTypeEnum.NONE:
        positionInfo = {
          liquidityType,
        };
        break;
      case LiquidityTypeEnum.SUFFICIENT: {
        const closingValue = Number(bestLiquidity[0]) * Number(position.amount);
        const pnl = calculatePnl(
          positionEntryValue,
          closingValue,
          position.side
        );
        positionInfo = {
          liquidityType,
          pnl,
          bestLiquidity: {
            iv: bestLiquidity[2]
              ? `${(Number(bestLiquidity[2]) * 100).toFixed(2)}%`
              : undefined,
            price: currency(bestLiquidity[0], {
              precision: getMarketPrecision(
                position.asset,
                position.instrument_type
              ).price_precision,
            }).format(),
          },
        };
        break;
      }
      case LiquidityTypeEnum.BLEND: // This is where RFQ should be created
      case LiquidityTypeEnum.INSUFFICIENT: {
        const closingValue = getValueOfContractsAmount(
          position.side === SideResponse.Buy
            ? SideResponse.Sell
            : SideResponse.Buy,
          liquidity as IPriceSize[],
          Number(position.amount)
        );
        // Size depends on blend or insufficient liquidity
        const totalSize = Number(position.amount);
        const size = Math.min(totalSize, totalLiquiditySize);
        const entryValue = size * Number(position.avg_entry_price);
        const pnl = calculatePnl(entryValue, closingValue, position.side);
        const avgIV =
          liquidity.reduce((prev, liq) => prev + (Number(liq[2]) || 0), 0) /
          size;

        // INSUFFICIENT means part of the position is left open. Compute the
        // unrealized PnL on the remainder at mark price so the user can see
        // the full picture (closed-portion realized + remaining unrealized).
        const remainingSize = Math.max(0, totalSize - size);
        const markPriceNum = Number(mark?.mark_price || position.mark_price || 0);
        const remainingUnrealizedPnl =
          liquidityType === LiquidityTypeEnum.INSUFFICIENT &&
          remainingSize > 0 &&
          markPriceNum > 0
            ? calculatePnl(
                remainingSize * Number(position.avg_entry_price),
                remainingSize * markPriceNum,
                position.side
              )
            : undefined;

        positionInfo = {
          liquidityType,
          pnl,
          averagePrice: currency(closingValue / size, {
            precision: getMarketPrecision(
              position.asset,
              position.instrument_type
            ).price_precision,
          }).format(),
          averageIV: avgIV ? `${(avgIV * 100).toFixed(2)}%` : undefined,
          ...(liquidityType === LiquidityTypeEnum.INSUFFICIENT
            ? {
                closedSize: size,
                totalSize,
                remainingSize,
                remainingUnrealizedPnl,
              }
            : {}),
        };
        break;
      }
      default:
        break;
    }
  }

  return positionInfo;
}
